import { useState, useEffect, useRef } from "react";
import { cn } from "@/lib/utils";
import { Check, X, Shield, Crosshair, ArrowDown, Zap } from "lucide-react";

interface LiveCommitSummaryProps {
  thesisText: string;
  killerCondition: string;
  reversibility?: string;
  optionChosen?: string;
  optionRejected?: string;
  dominantRisk?: string;
  coreAssumptions?: string[];
  optionSelected?: string;
  className?: string;
}

interface QualityCheck {
  id: string;
  label: string;
  passed: boolean;
}

const checkFalsifiable = (text: string): boolean => {
  if (!text || text.length < 10) return false;
  const softWords = /\b(gefühl|klarheit|besser|gefühlt|irgendwie|vielleicht|eventuell|hoffentlich)\b/i;
  if (softWords.test(text)) return false;
  const markers = /\b(wenn|falls|sofern|sobald|solange|vorausgesetzt|mindestens|höchstens|innerhalb|bis zum|spätestens|frühestens|>=|<=|>|<)\b/i;
  return markers.test(text);
};

const checkMeasurable = (text: string): boolean => {
  if (!text || text.length < 10) return false;
  const measurableMarkers = /\b(\d+[%]?|\d+\s*(kunden?|nutzer|user|prozent|euro|€|\$|leads?|deals?|tickets?|anfragen|conversions?|sign-?ups?|downloads?|tage?|wochen?|monate?)|\bkpi\b|\bmetrik\b|\bmessbar\b|\bquote\b|\brate\b|\banteil\b)\b/i;
  return measurableMarkers.test(text);
};

export const LiveCommitSummary = ({
  thesisText,
  killerCondition,
  reversibility,
  optionChosen,
  optionRejected,
  dominantRisk,
  optionSelected,
  className,
}: LiveCommitSummaryProps) => {
  const [checks, setChecks] = useState<QualityCheck[]>([]);
  const debounceRef = useRef<number | null>(null);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setChecks([
        { id: "falsifiable", label: "Falsifizierbar", passed: checkFalsifiable(killerCondition) },
        { id: "measurable", label: "Messbar", passed: checkMeasurable(killerCondition) },
      ]);
    }, 300);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [killerCondition]);

  const hasKillerInput = killerCondition.trim().length > 0;
  const passedCount = checks.filter(c => c.passed).length;
  const allPassed = passedCount === 2 && hasKillerInput;

  return (
    <div className={cn("space-y-3", className)}>
      <div className="relative overflow-hidden rounded-2xl border border-primary/20 bg-card shadow-sm">
        <div className="h-0.5 bg-gradient-to-r from-primary/60 via-primary to-primary/60" />

        <div className="p-4 space-y-3.5">
          <div className="flex items-center gap-2">
            <Zap className="w-3.5 h-3.5 text-primary" />
            <h4 className="text-[10px] font-bold text-primary uppercase tracking-widest">
              Entscheidungsmodell
            </h4>
          </div>

          {/* Position – compact */}
          {(optionSelected || thesisText) && (
            <div className="space-y-1">
              <p className="text-[10px] font-bold text-primary uppercase tracking-wider">Wette</p>
              <p className="text-xs font-bold text-foreground line-clamp-2">
                {optionSelected || thesisText}
              </p>
            </div>
          )}

          {/* Dynamic Impact Chain */}
          {hasKillerInput ? (
            <div className={cn(
              "space-y-2 p-3 rounded-xl border transition-all duration-500",
              allPassed
                ? "bg-primary/[0.04] border-primary/20"
                : "bg-muted/20 border-border/40"
            )}>
              <div className="flex items-center gap-1.5">
                <Crosshair className="w-3 h-3 text-primary" />
                <p className="text-[10px] font-bold text-primary uppercase tracking-widest">Wenn</p>
              </div>
              <p className="text-xs leading-relaxed text-foreground font-medium">
                {killerCondition}
              </p>

              <div className="flex justify-center py-1">
                <ArrowDown className={cn(
                  "w-4 h-4 transition-colors duration-300",
                  allPassed ? "text-primary" : "text-muted-foreground/30"
                )} />
              </div>

              <div className="space-y-1.5">
                {optionChosen && (
                  <div className="flex items-center gap-2">
                    <div className={cn("w-1.5 h-1.5 rounded-full", allPassed ? "bg-destructive" : "bg-muted-foreground/30")} />
                    <span className={cn("text-[11px] font-medium", allPassed ? "text-destructive" : "text-muted-foreground/50")}>
                      → {optionChosen} wird ungültig
                    </span>
                  </div>
                )}
                <div className="flex items-center gap-2">
                  <div className={cn("w-1.5 h-1.5 rounded-full", allPassed ? "bg-destructive" : "bg-muted-foreground/30")} />
                  <span className={cn("text-[11px] font-medium", allPassed ? "text-destructive" : "text-muted-foreground/50")}>
                    → These ist widerlegt
                  </span>
                </div>
                {optionRejected && (
                  <div className="flex items-center gap-2">
                    <div className={cn("w-1.5 h-1.5 rounded-full", allPassed ? "bg-primary" : "bg-muted-foreground/30")} />
                    <span className={cn("text-[11px] font-medium", allPassed ? "text-primary" : "text-muted-foreground/50")}>
                      → Fokus wechselt zu {optionRejected}
                    </span>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="space-y-1 p-3 rounded-xl bg-muted/10 border border-border/30">
              <div className="flex items-center gap-1.5">
                <Crosshair className="w-3 h-3 text-muted-foreground/40" />
                <p className="text-[10px] font-semibold text-muted-foreground/40 uppercase tracking-wider">Kipppunkt</p>
              </div>
              <p className="text-[11px] text-muted-foreground/40 italic">
                Definiere links den Kipppunkt.
              </p>
            </div>
          )}

          {/* Reversibility */}
          {reversibility && reversibility !== "–" && (
            <div className="flex items-center justify-between pt-1 border-t border-border/20">
              <div className="flex items-center gap-1.5">
                <Shield className="w-3 h-3 text-muted-foreground" />
                <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">Reversibilität</p>
              </div>
              <span className={cn(
                "inline-flex items-center text-[10px] font-bold px-2 py-0.5 rounded-md border uppercase tracking-wider",
                reversibility === "irreversibel" ? "bg-destructive/10 text-destructive border-destructive/20" :
                reversibility === "schwer" ? "bg-destructive/5 text-destructive/80 border-destructive/15" :
                "bg-primary/5 text-primary border-primary/15"
              )}>
                {reversibility}
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Quality Checks */}
      {hasKillerInput && (
        <div className="rounded-2xl border border-border/40 bg-card shadow-sm p-4 space-y-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Shield className="w-3.5 h-3.5 text-primary" />
              <span className="text-[10px] font-bold text-primary uppercase tracking-widest">Qualität</span>
            </div>
            <div className={cn(
              "text-[11px] font-bold tabular-nums px-2 py-0.5 rounded-md",
              passedCount === 2 
                ? "bg-primary/10 text-primary" 
                : passedCount >= 1 
                  ? "bg-secondary/10 text-secondary" 
                  : "bg-muted/30 text-muted-foreground"
            )}>
              {passedCount}/2
            </div>
          </div>

          <div className="h-1.5 rounded-full bg-muted/30 overflow-hidden">
            <div 
              className={cn(
                "h-full rounded-full transition-all duration-500 ease-out",
                passedCount === 2 ? "bg-primary" : passedCount === 1 ? "bg-secondary" : "bg-muted-foreground/20"
              )}
              style={{ width: `${(passedCount / 2) * 100}%` }}
            />
          </div>

          <div className="space-y-1.5">
            {checks.map((check) => (
              <div key={check.id} className="flex items-center gap-2.5 py-0.5">
                <div className={cn(
                  "w-4 h-4 rounded-md flex items-center justify-center flex-shrink-0 transition-all duration-300",
                  check.passed ? "bg-primary/15 text-primary" : "bg-muted/30 text-muted-foreground/30"
                )}>
                  {check.passed ? <Check className="w-2.5 h-2.5" /> : <X className="w-2.5 h-2.5" />}
                </div>
                <span className={cn(
                  "text-xs font-medium transition-colors",
                  check.passed ? "text-foreground" : "text-muted-foreground/50"
                )}>
                  {check.label}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
